import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash/startCase';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import { theme } from 'theme';
import { useReportPage } from 'shared/hooks';
import { humanize } from 'shared/functions';
import { ErrorBoundary } from 'shared/errors';
import { Loader } from 'ui';
import { DoughnutChart, BoxPlotChart } from 'ui/Charts';

const componentWidth = useReportPage ? 200 : 600;
const useStyles = makeStyles({
  boxPlotLabel: {
    fontSize: 12,
    color: theme.palette.primary.main,
    marginLeft: 130,
    maxWidth: componentWidth,
  },
});

const { darkCyan, softOrange, verySoftPink, veryLightBlue, black } = theme.palette.custom;
const colors = [darkCyan, softOrange, verySoftPink, veryLightBlue, black];
const parseDonutData = data =>
  Object.keys(data).reduce((prev, next) => {
    return {
      ...prev,
      [next]: (data[next] * 100).toFixed(1),
    };
  }, {});
const labels = {
  companyValueTooltip: 'outliersTooltip',
  companyValueGraph: 'outliers',
  peers25Value: 'quartile1',
  peers75Value: 'quartile3',
  peersMaxValue: 'whiskerHigh',
  peersMedianValue: 'quartile2',
  peersMinValue: 'whiskerLow',
};
const parseBoxPlotData = data =>
  Object.keys(data)
    .map(key => ({
      ...Object.keys(data[key]).reduce(
        (prev, next) => ({
          ...prev,
          [labels[next]]: data[key][next],
        }),
        {},
      ),
      title: startCase(key),
    }))
    .map((item, index) => ({ ...item, color: colors[index] }));

// eslint-disable-next-line no-unused-vars
const ESGImpactDistribution = React.memo(({ data, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="h6">{t('Distribution of impacts')}</Typography>
        <Box py={2}>
          <Divider />
        </Box>
      </Box>
      {loading ? (
        <Loader center />
      ) : (
        Object.keys(data).map(key =>
          data[key].boxplotData || data[key].donutData ? (
            <React.Fragment key={key}>
              <Box px={12} py={useReportPage ? 0 : 2}>
                <Typography variant={useReportPage ? 'body1' : 'h5'}>
                  <strong>{humanize(key)}</strong>
                </Typography>
              </Box>
              <Box p={2}>
                <Grid container justifyContent={useReportPage ? 'flex-start' : 'space-between'}>
                  <Grid item xs={useReportPage ? 6 : 4}>
                    {data[key].donutData ? (
                      <ErrorBoundary>
                        <DoughnutChart
                          data={parseDonutData(data[key].donutData)}
                          width={400}
                          height={300}
                          margin={0}
                          colors={colors}
                        />
                      </ErrorBoundary>
                    ) : null}
                  </Grid>
                  {useReportPage ? null : <Divider orientation="vertical" flexItem />}
                  <Grid item xs={useReportPage ? 6 : 7}>
                    {data[key].boxplotData ? (
                      <ErrorBoundary>
                        <div className={classes.boxPlotLabel}>{t('In Cents of EUR')}</div>
                        <BoxPlotChart
                          min={-0.5}
                          max={10}
                          height={useReportPage ? 15 : undefined}
                          width={componentWidth}
                          stats={parseBoxPlotData(data[key].boxplotData)}
                        />
                      </ErrorBoundary>
                    ) : null}
                  </Grid>
                </Grid>
              </Box>
            </React.Fragment>
          ) : null,
        )
      )}
    </Paper>
  );
});

ESGImpactDistribution.propTypes = {
  data: PropTypes.shape({
    environment: PropTypes.shape({
      boxplotData: PropTypes.object,
      donutData: PropTypes.object,
    }),
    governance: PropTypes.shape({
      boxplotData: PropTypes.object,
      donutData: PropTypes.object,
    }),
    social: PropTypes.shape({
      boxplotData: PropTypes.object,
      donutData: PropTypes.object,
    }),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ESGImpactDistribution;
